import React, { useContext, useEffect, useState } from "react";

import { Download } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  useTheme,
} from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import mainStyles from "styles";

import { ProductAdmin } from "@safetyculture/s12-apis-connect-web/s12/contentlibrary/admin/v1/response_pb";
import { ProductType as S12ProductType } from "@safetyculture/s12-apis-connect-web/s12/contentlibrary/v1/product_pb";
import { Environment } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import { ContentLibrarySearchSelectionLimit } from "./constants";
import ContentUploader from "./content-uploader";
import { ProductDialog } from "./product-dialog";
import useStyles from "./styles";
import {
  PaginationOption,
  ProductStateSearch,
  deleteProduct,
  exportProducts,
  searchContentLibrary,
} from "api/endpoints/content-library";
import { ContentPreview } from "components/search/components/content-preview";
import ConfirmDialog from "components/shared/confirm-dialog";
import DisplayError from "components/shared/display-error";
import { DisplayToastAlert } from "components/shared/display-toast-alert";
import KebabMenu from "components/shared/kebab-menu";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { downloadFileFromUrl } from "helpers/download-file";
import { getAppURLByEnvironment, getEnvironment } from "helpers/environment";
import { validateS12ID, validateUUID } from "helpers/s12-helpers";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";

export type SortOption = {
  option: "downloadOrder" | "createdAt";
  order: "asc" | "desc";
};

const getProductTypeDisplayName = (type: S12ProductType): string => {
  switch (type) {
    case S12ProductType.TEMPLATE:
      return "Template";
    case S12ProductType.COURSE:
      return "Course";
    case S12ProductType.COLLECTION:
      return "Collection";
    case S12ProductType.UNSPECIFIED:
      return "Unspecified";
    default:
      return "Unknown";
  }
};

const initialFilters = {
  productState: "all" as ProductStateSearch,
  productType: 0,
  title: "",
  sourceContentId: "",
  orgId: "",
  slug: "",
  publisherName: "",
};

const ContentLibrarySearch: React.FC = () => {
  const classes = mainStyles();
  const clSearchClasses = useStyles();
  const theme = useTheme();
  const [filters, setFilters] = useState(initialFilters);
  const [sortOption, setSortOption] = useState<SortOption>({
    option: "downloadOrder",
    order: "desc",
  });

  const updateFilter = <K extends keyof typeof initialFilters>(
    key: K,
    value: typeof initialFilters[K]
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setErrorValidationMessages([]);
  };
  const [totalResults, setTotalResults] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState("");

  const [paginationOption, setPaginationOptions] = useState<PaginationOption>({
    pageSize: 25,
    offset: 0,
  });
  const [searchResults, setSearchResults] = useState<ProductAdmin[]>([]);
  const [showPreviewInfo, setShowPreviewInfo] = useState<
    | {
        id: string;
        orgId: string;
        show: boolean;
      }
    | undefined
  >(undefined);

  const [isLoading, setIsLoading] = useState(false);
  const [errorValidationMessages, setErrorValidationMessages] = useState<
    string[]
  >([]);
  const [selectedProductTitle, setSelectedProductTitle] = useState("");

  const history = useHistory();
  const { hash } = useLocation();
  const setHash = (h: string) => history.replace({ hash: h });
  const clearHash = () => history.replace({ hash: "" });

  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(
    new Set()
  );

  const { showError } = useContext(SnackbarContext);

  const hasSelection = selectedProductIds.size > 0;
  const selectedCount = selectedProductIds.size;
  const isAtLimit = selectedCount >= ContentLibrarySearchSelectionLimit;

  const isProductSelected = (id: string) => selectedProductIds.has(id);

  const currentPageIds = searchResults.map((r) => r.id);
  const currentPageSelectedCount = currentPageIds.filter((id) =>
    isProductSelected(id)
  ).length;
  const isHeaderChecked =
    searchResults.length > 0 &&
    currentPageSelectedCount === searchResults.length;
  const isHeaderIndeterminate =
    currentPageSelectedCount > 0 &&
    currentPageSelectedCount < searchResults.length;

  const environment = getEnvironment();
  const isDevOrSlate =
    environment !== Environment.SANDPIT &&
    environment !== Environment.PRODUCTION;

  const handleDeleteProduct = () => {
    deleteProduct({ productId: selectedProduct })
      .then(() => {
        setHash("#delete-success");
        setSelectedProduct("");
        setSelectedProductTitle("");
        handleSearchContent();
      })
      .catch((error) => {
        showError(error as GrpcError);
      });
  };

  const handleToggleProduct = (id: string) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < ContentLibrarySearchSelectionLimit) {
        next.add(id);
      }
      return next;
    });
  };

  const handleTogglePageAll = (checked: boolean) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      currentPageIds.forEach((id) => {
        if (checked) {
          if (next.size < ContentLibrarySearchSelectionLimit) {
            next.add(id);
          }
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedProductIds(new Set());
  };

  const isInvalidId = (value: string) =>
    !!value && !validateUUID(value) && !validateS12ID(value);

  const handleValidation = (): string[] => {
    if (isInvalidId(filters.sourceContentId) || isInvalidId(filters.orgId)) {
      return ["Please enter a valid UUID or s12 ID"];
    }
    return [];
  };

  const handleSearchContent = async () => {
    setIsLoading(true);
    const errors = handleValidation();
    if (errors.length > 0) {
      setErrorValidationMessages(errors);
      setIsLoading(false);
      return;
    }
    try {
      const res = await searchContentLibrary({
        sortOption,
        paginationOption,
        filterParams: filters,
      });
      setSearchResults(res.products);
      setTotalResults(res.searchTotal);
    } catch (error) {
      setSearchResults([]);
      showError(error as GrpcError);
    } finally {
      setIsLoading(false);
    }
  };

  const clearForm = () => {
    setFilters(initialFilters);
    setErrorValidationMessages([]);
    setShowPreviewInfo(undefined);
    handleClearSelection();
    setPaginationOptions((prev) => ({ ...prev, offset: 0 }));
  };

  const handleSetSearchRanking = (sortBy: "downloadOrder" | "createdAt") => {
    if (sortBy === sortOption.option) {
      setSortOption((prev) => {
        return {
          ...prev,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      });
    } else {
      setSortOption((prev) => {
        return {
          option:
            prev.option === "downloadOrder" ? "createdAt" : "downloadOrder",
          order: "desc",
        };
      });
    }
  };

  const exportCSV = () => {
    exportProducts(filters, Array.from(selectedProductIds))
      .then((response) => {
        downloadFileFromUrl("", response.signedExportsUrl);
      })
      .catch(() => {
        setHash("#export-failed");
      });
  };

  useEffect(() => {
    setShowPreviewInfo(undefined);
    handleSearchContent();
  }, [sortOption, paginationOption]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <Box
        component="form"
        sx={classes.spaceSearch}
        noValidate
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          setShowPreviewInfo(undefined);
          handleClearSelection();
          setPaginationOptions({
            offset: 0,
            pageSize: paginationOption.pageSize,
          });
        }}
        autoComplete="off"
      >
        <FormControl variant="outlined" size="small" sx={classes.formControl}>
          <InputLabel id="select-prod-state-select-label">
            Product State
          </InputLabel>
          <Select
            labelId="select-prod-state-select-label"
            id="select-prod-state"
            label="Product State"
            value={filters.productState}
            onChange={(e) => {
              const updatedValue = e.target.value as ProductStateSearch;
              updateFilter("productState", updatedValue);
            }}
            sx={{ textTransform: "capitalize" }}
          >
            {["all", "published", "unpublished"].map((state) => (
              <MenuItem
                key={state}
                value={state}
                sx={{ textTransform: "capitalize" }}
              >
                {state}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" sx={classes.formControl}>
          <InputLabel id="select-prod-type-label">Product Type</InputLabel>
          <Select
            labelId="select-prod-type-label"
            id="select-prod-type"
            label="Product Type"
            value={filters.productType}
            onChange={(e: SelectChangeEvent<number>) => {
              updateFilter("productType", e.target.value as number);
            }}
            data-testid="prod-type-select"
          >
            <MenuItem value={0}>All</MenuItem>
            <MenuItem value={S12ProductType.TEMPLATE}>Template</MenuItem>
            <MenuItem value={S12ProductType.COURSE}>Course</MenuItem>
            <MenuItem value={S12ProductType.COLLECTION}>Collection</MenuItem>
          </Select>
        </FormControl>
        <TextField
          sx={classes.textInput}
          label="Title"
          value={filters.title}
          onChange={(event) => {
            updateFilter("title", event.target.value);
          }}
          variant="outlined"
          size="small"
        />
        <TextField
          sx={classes.textInput}
          label="Source Content ID"
          value={filters.sourceContentId}
          error={
            errorValidationMessages.length > 0 &&
            isInvalidId(filters.sourceContentId)
          }
          onChange={(event) => {
            updateFilter("sourceContentId", event.target.value);
          }}
          variant="outlined"
          size="small"
        />
        <TextField
          sx={classes.textInput}
          label="Org ID"
          value={filters.orgId}
          error={
            errorValidationMessages.length > 0 && isInvalidId(filters.orgId)
          }
          onChange={(event) => {
            updateFilter("orgId", event.target.value);
          }}
          variant="outlined"
          size="small"
        />
        <TextField
          sx={classes.textInput}
          label="Slug"
          value={filters.slug}
          onChange={(event) => {
            updateFilter("slug", event.target.value);
          }}
          variant="outlined"
          size="small"
        />
        <TextField
          sx={classes.textInput}
          label="Publisher Name"
          value={filters.publisherName}
          onChange={(event) => {
            updateFilter("publisherName", event.target.value);
          }}
          variant="outlined"
          size="small"
        />
        <Button
          variant="contained"
          color="primary"
          type="submit"
          data-testid="content-search-btn"
        >
          Search
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={clearForm}
          data-anchor="doc-search-clear-btn"
          data-testid="doc-search-clear-btn"
        >
          Clear
        </Button>
      </Box>
      <DisplayError errorMessage={errorValidationMessages.join(". ")} />
      <div style={{ display: "flex" }}>
        <TableContainer component={Paper} style={{ flexBasis: "100%" }}>
          <Table
            sx={clSearchClasses.contentLibraryTable}
            aria-label="content library search results"
          >
            <colgroup>
              <col style={{ width: "3%" }} /> {/* Checkbox */}
              <col style={{ width: "14%" }} /> {/* Title */}
              <col style={{ width: "10%" }} /> {/* Source content ID */}
              <col style={{ width: "10%" }} /> {/* Product ID */}
              <col style={{ width: "7%" }} /> {/* Product State */}
              <col style={{ width: "9%" }} /> {/* Publisher Name */}
              <col style={{ width: "10%" }} /> {/* Type */}
              <col style={{ width: "10%" }} /> {/* Slug */}
              <col style={{ width: "10%" }} /> {/* Downloads */}
              <col style={{ width: "10%" }} /> {/* Created */}
              <col style={{ width: "7%" }} /> {/* Action */}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell colSpan={5}>
                  Content Items ({totalResults})
                </TableCell>
                <TableCell
                  colSpan={6}
                  style={{
                    textAlign: "end",
                  }}
                >
                  <Button
                    startIcon={<Download />}
                    variant="outlined"
                    onClick={exportCSV}
                    data-testid="csv-export-btn"
                  >
                    {selectedProductIds.size > 0
                      ? `Export ${selectedProductIds.size} selected`
                      : "Export all"}
                  </Button>
                </TableCell>
              </TableRow>
              {hasSelection && (
                <TableRow sx={clSearchClasses.selectionBanner}>
                  <TableCell colSpan={11}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <span>
                        <b>{selectedCount}</b>{" "}
                        {selectedCount === 1 ? "product" : "products"} selected
                      </span>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={handleClearSelection}
                        data-testid="clear-selection-btn"
                      >
                        Clear selection
                      </Link>
                      {isAtLimit && (
                        <span style={{ color: theme.palette.warning.main }}>
                          Selection limit of{" "}
                          {ContentLibrarySearchSelectionLimit} reached. Please
                          narrow your search filters.
                        </span>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    disabled={isAtLimit && !isHeaderChecked}
                    indeterminate={isHeaderIndeterminate}
                    checked={isHeaderChecked}
                    onChange={(e) => handleTogglePageAll(e.target.checked)}
                    inputProps={{ "aria-label": "select all on page" }}
                    data-testid="select-all-page-checkbox"
                  />
                </TableCell>
                <TableCell>Title - click to preview</TableCell>
                <TableCell>Source Content ID</TableCell>
                <TableCell>Product ID</TableCell>
                <TableCell>Product State</TableCell>
                <TableCell>Publisher Name</TableCell>
                <TableCell>Content Type</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell width={140}>
                  <Button
                    sx={clSearchClasses.searchFilterButton}
                    style={
                      sortOption.option === "downloadOrder"
                        ? { color: theme.palette.primary.main }
                        : {}
                    }
                    data-testid="sort-by-downloads-btn"
                    onClick={() => {
                      handleSetSearchRanking("downloadOrder");
                    }}
                  >
                    Downloads
                    <Box
                      component="span"
                      sx={clSearchClasses.rankingCaretAndCancel}
                    >
                      {sortOption.option === "downloadOrder" &&
                        sortOption.order === "asc" &&
                        "▲"}
                      {sortOption.option === "downloadOrder" &&
                        sortOption.order === "desc" &&
                        "▼"}
                    </Box>
                  </Button>
                </TableCell>
                <TableCell width={140}>
                  <Button
                    sx={clSearchClasses.searchFilterButton}
                    style={
                      sortOption.option === "createdAt"
                        ? { color: theme.palette.primary.main }
                        : {}
                    }
                    data-testid="sort-by-date-published-btn"
                    onClick={() => {
                      handleSetSearchRanking("createdAt");
                    }}
                  >
                    Created
                    <Box
                      component="span"
                      sx={clSearchClasses.rankingCaretAndCancel}
                    >
                      {sortOption.option === "createdAt" &&
                        sortOption.order === "asc" &&
                        "▲"}
                      {sortOption.option === "createdAt" &&
                        sortOption.order === "desc" &&
                        "▼"}
                    </Box>
                  </Button>
                </TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={11} style={{ textAlign: "center" }}>
                    <CircularProgress
                      color="primary"
                      variant="indeterminate"
                      data-testid="table-loading-spinner"
                      thickness={3}
                      size={30}
                    />
                  </TableCell>
                </TableRow>
              ) : !searchResults || searchResults.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11}>
                    <b>{"No results found."}</b>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {searchResults.map((content) => {
                    return (
                      <TableRow key={content.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            disabled={
                              isAtLimit && !isProductSelected(content.id)
                            }
                            checked={isProductSelected(content.id)}
                            onChange={() => handleToggleProduct(content.id)}
                          />
                        </TableCell>
                        <TableCell
                          style={{
                            wordBreak: "break-word",
                          }}
                        >
                          {content.type === S12ProductType.COURSE ? (
                            <Link
                              href={`${getAppURLByEnvironment()}/training/manage/courseware?show=content-library&course-id=${
                                content.contentId
                              }`}
                              target="_blank"
                              rel="noreferrer noopener"
                              color="primary"
                            >
                              {content.title}
                            </Link>
                          ) : (
                            <Button
                              sx={clSearchClasses.titleButton}
                              key={content.contentId}
                              style={{
                                textTransform: "none",
                              }}
                              data-testid="template-row-title"
                              onClick={() => {
                                if (
                                  content.contentId !== showPreviewInfo?.id &&
                                  content.publisher?.orgId
                                ) {
                                  setShowPreviewInfo({
                                    id: content.id,
                                    orgId: content.publisher.orgId,
                                    show: true,
                                  });
                                }
                              }}
                            >
                              {content.title}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>{content.contentId}</TableCell>
                        <TableCell>{content.id}</TableCell>
                        <TableCell>
                          {content.isPublished ? "Published" : "Unpublished"}
                        </TableCell>
                        <TableCell>{content.publisher?.title}</TableCell>
                        <TableCell>
                          {getProductTypeDisplayName(content.type)}
                        </TableCell>
                        <TableCell>{content.slug}</TableCell>
                        <TableCell>{content.downloadCount}</TableCell>
                        <TableCell>
                          {content.createdAt
                            ? formatDateTimeDisplay(
                                convertTimestampToDate(content.createdAt)
                              )
                            : null}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          <KebabMenu>
                            <MenuItem
                              data-testid="publish-toggle-btn"
                              onClick={() => {
                                if (content.isPublished) {
                                  setHash("#unpublish");
                                } else {
                                  setHash("#publish");
                                }
                                setSelectedProduct(content.id);
                              }}
                            >
                              {content.isPublished ? "Unpublish" : "Publish"}
                            </MenuItem>
                            {!content.isPublished ? (
                              <MenuItem
                                onClick={() => {
                                  setSelectedProduct(content.id);
                                  setSelectedProductTitle(content.title);
                                  setHash("#delete");
                                }}
                              >
                                Delete
                              </MenuItem>
                            ) : (
                              // <Menu> does not allow empty fragments
                              <span />
                            )}
                            {content.isPublished ? (
                              <MenuItem
                                disabled={isDevOrSlate}
                                onClick={() => {
                                  window.open(
                                    `${getPublicListingBaseUrl()}/${
                                      content.industrySlug
                                    }/${content.slug}`,
                                    "_blank"
                                  );
                                }}
                              >
                                View public listing
                              </MenuItem>
                            ) : (
                              // <Menu> does not allow empty fragments
                              <span />
                            )}
                          </KebabMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  page={paginationOption.offset}
                  onPageChange={(_, newPageNumber) => {
                    setPaginationOptions((prev) => {
                      return {
                        ...prev,
                        offset: newPageNumber,
                      };
                    });
                  }}
                  count={totalResults}
                  rowsPerPage={paginationOption.pageSize}
                  rowsPerPageOptions={[25, 50, 100]}
                  onRowsPerPageChange={(e) => {
                    setPaginationOptions((prev) => {
                      return {
                        ...prev,
                        pageSize: Number(e.target.value),
                      };
                    });
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
        {showPreviewInfo?.show && showPreviewInfo?.id && (
          <TableContainer
            sx={clSearchClasses.previewFrameContainer}
            style={{ flexBasis: "450px" }}
          >
            <Box sx={clSearchClasses.previewFrame}>
              <ContentPreview
                id={showPreviewInfo.id}
                publisherOrgId={showPreviewInfo.orgId}
              />
            </Box>
            <Button
              sx={clSearchClasses.hidePreviewButton}
              onClick={() => {
                setShowPreviewInfo(undefined);
              }}
            >
              <CloseIcon />
            </Button>
          </TableContainer>
        )}
      </div>
      <ContentUploader />
      <ProductDialog
        open={hash === "#unpublish"}
        forPublish={false}
        productId={selectedProduct}
        onClose={() => {
          clearHash();
          setSelectedProduct("");
        }}
        onSuccess={() => {
          setHash("#unpublish-success");
          setSelectedProduct("");
        }}
      />
      <ProductDialog
        open={hash === "#publish"}
        forPublish={true}
        productId={selectedProduct}
        onClose={() => {
          clearHash();
          setSelectedProduct("");
        }}
        onSuccess={() => {
          setHash("#publish-success");
          setSelectedProduct("");
        }}
      />
      <DisplayToastAlert
        message="Unpublish request was successful but it may take 15 minutes to process in the server. Please come back later."
        open={hash === "#unpublish-success"}
        severity="success"
        onClose={clearHash}
      />
      <DisplayToastAlert
        message="Publish request was successful but it may take 15 minutes to process in the server. Please come back later."
        open={hash === "#publish-success"}
        severity="success"
        onClose={clearHash}
      />
      <ConfirmDialog
        title={`Are you sure you want to delete '${selectedProductTitle}'?`}
        open={hash === "#delete"}
        setOpen={(isOpen: boolean) => {
          if (!isOpen) clearHash();
        }}
        onConfirm={() => handleDeleteProduct()}
        onCancel={() => {
          setSelectedProduct("");
          setSelectedProductTitle("");
        }}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Delete"
      />
      <DisplayToastAlert
        message="Product deleted successfully."
        open={hash === "#delete-success"}
        severity="success"
        onClose={clearHash}
      />
      <DisplayToastAlert
        message="Export to CSV failed. Please try again."
        open={hash === "#export-failed"}
        severity="error"
        onClose={clearHash}
      />
    </div>
  );
};

function getPublicListingBaseUrl() {
  const env = getEnvironment();
  switch (env) {
    case Environment.SANDPIT:
      return "https://sandpit.content-library.scvercel.dev/library";
    case Environment.PRODUCTION:
      return "https://safetyculture.com/library";
    default:
      return null;
  }
}

export default ContentLibrarySearch;
